import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import API from '../../services/config/api';
import foreseeLogo from '../../NovaWidget/assets/foresee-eagle.png';
import '../../Auth/SignIn.css';
import LoginOtpStep from '../../Auth/LoginOtpStep';

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="3"/>
      <path d="M3.5 7.5l8.5 6 8.5-6"/>
    </svg>
  );
}
function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4.5" y="10.5" width="15" height="10" rx="3"/>
      <path d="M8 10.5V8a4 4 0 018 0v2.5M12 15v2"/>
    </svg>
  );
}
function AlertCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M12 7.5v5.5M12 16.4v.1"/>
    </svg>
  );
}
function CheckCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M22 4 12 14.01l-3-3"/>
    </svg>
  );
}
function ArrowRightIcon() {
  return (
    <svg className="lg-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  );
}

const MODULES = [
  {
    title: 'Risk Register',
    code: 'RISK',
    tag: 'Full lifecycle tracking',
    icon: (
      <>
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
        <rect x="9" y="3" width="6" height="4" rx="1"/>
        <path d="M9 12h6M9 16h4"/>
      </>
    ),
  },
  {
    title: 'Treatments',
    code: 'TRT',
    tag: 'Mitigate every finding',
    icon: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <path d="M9 12l2 2 4-4"/>
      </>
    ),
  },
  {
    title: 'KRI Tracking',
    code: 'KRI',
    tag: 'Early warning signals',
    icon: (
      <>
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
        <polyline points="16 7 22 7 22 13"/>
      </>
    ),
  },
  {
    title: 'Acceptance Workflow',
    code: 'ACW',
    tag: 'Severity-tiered approvals',
    icon: (
      <>
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <polyline points="16 11 18 13 22 9"/>
      </>
    ),
  },
];

function Clouds() {
  return (
    <div className="lg-clouds" aria-hidden="true">
      <div className="lg-cloud lg-cloud--far">
        <svg width="2000" height="1080" viewBox="0 0 2000 1080">
          <filter id="rp2-cf" filterUnits="userSpaceOnUse" x="0" y="0" width="2000" height="1080" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.0016 0.0062" numOctaves={5} seed={4} stitchTiles="stitch"/>
            <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  3.4 0 0 0 -1.45"/>
          </filter>
          <rect width="2000" height="1080" filter="url(#rp2-cf)"/>
        </svg>
      </div>
      <div className="lg-cloud lg-cloud--near">
        <svg width="2000" height="1080" viewBox="0 0 2000 1080">
          <filter id="rp2-cn" filterUnits="userSpaceOnUse" x="0" y="0" width="2000" height="1080" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.0024 0.0078" numOctaves={6} seed={11} stitchTiles="stitch"/>
            <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  3.8 0 0 0 -1.62"/>
            <feGaussianBlur stdDeviation="1.2"/>
          </filter>
          <rect width="2000" height="1080" filter="url(#rp2-cn)"/>
        </svg>
      </div>
    </div>
  );
}

function Field({ name, type, label, icon, value, onChange, trailing, autoComplete }) {
  return (
    <div className="lg-field">
      <div className="lg-control">
        <span className="lg-field-ico">{icon}</span>
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder=" "
          autoComplete={autoComplete}
          maxLength={name === 'email' ? 50 : 30}
        />
        <label htmlFor={name}>{label}</label>
        {trailing && <span className="lg-field-end">{trailing}</span>}
      </div>
    </div>
  );
}

function EyeButton({ show, onToggle }) {
  return (
    <button
      type="button"
      className="lg-eye"
      aria-label={show ? 'Hide password' : 'Show password'}
      aria-pressed={show}
      onMouseDown={e => e.preventDefault()}
      onClick={onToggle}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"/>
        <circle cx="12" cy="12" r="3"/>
        {!show && <path d="M4 4l16 16"/>}
      </svg>
    </button>
  );
}

function trackPointer(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--x', `${((e.clientX - rect.left) / rect.width * 100).toFixed(1)}%`);
  e.currentTarget.style.setProperty('--y', `${((e.clientY - rect.top) / rect.height * 100).toFixed(1)}%`);
}

function RiskPortalAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  const isExplicitLogin = location.pathname === '/risk-portal/login';

  const [mode, setMode] = useState(inviteToken ? 'register' : 'login');
  const [form, setForm] = useState({ email: '', password: '', confirm_password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [otpChallenge, setOtpChallenge] = useState(null);

  useEffect(() => {
    if (isExplicitLogin) return;

    const portalToken = localStorage.getItem('risk_portal_token');
    if (portalToken && !inviteToken) {
      navigate('/risk-portal');
      return;
    }

    const mainToken = sessionStorage.getItem('access_token');
    if (mainToken && !inviteToken) {
      const userProfile = JSON.parse(localStorage.getItem('user_profile') || '{}');
      localStorage.setItem('risk_portal_token', mainToken);
      localStorage.setItem('risk_portal_user', JSON.stringify({
        email: userProfile.emailId || userProfile.email || '',
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        organization_id: userProfile.organization_id || '',
      }));
      sessionStorage.setItem('rp_from_dashboard', '1');
      navigate('/risk-portal');
    }
  }, [navigate, inviteToken, isExplicitLogin]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const completeLogin = (authData) => {
    sessionStorage.setItem('access_token', authData.access_token);
    if (authData.refresh_token) {
      sessionStorage.setItem('refresh_token', authData.refresh_token);
      localStorage.setItem('risk_portal_refresh', authData.refresh_token);
    }
    localStorage.setItem('user_profile', JSON.stringify(authData.user || {}));
    localStorage.setItem('risk_portal_token', authData.access_token);
    localStorage.setItem('risk_portal_user', JSON.stringify({
      email: authData.email || form.email,
      role: authData.role || '',
      first_name: authData.first_name || '',
      last_name: authData.last_name || '',
      organization_id: authData.organization_id || '',
    }));
    sessionStorage.setItem('rp_from_dashboard', '0');
    navigate('/risk-portal');
  };

  const restartLogin = () => {
    setOtpChallenge(null);
    setError('');
  };

  const handleLogin = async () => {
    if (loading) return;
    if (!form.email || !form.password) { setError('Email and password are required.'); return; }
    setLoading(true); setError('');
    try {
      const axios = (await import('axios')).default;
      const authRes = await axios.post(
        API.RISK_PORTAL_LOGIN,
        { email: form.email, password: form.password },
        { headers: { 'Content-Type': 'application/json', 'Authorization': 'Basic YmFzZWFkbWluOmRlZmF1bHRANDU2' } }
      );
      const authData = authRes?.data?.response_data?.data;

      if (authData?.otp_required) {
        setForm(prev => ({ ...prev, password: '' }));
        setOtpChallenge(authData);
        setLoading(false);
        return;
      }

      if (!authData?.access_token) {
        setError(authRes?.data?.response_data?.message || 'Invalid email or password.');
        setLoading(false);
        return;
      }

      completeLogin(authData);
    } catch (e) {
      const status = e?.response?.status;
      const errCode = e?.response?.data?.error?.error_code;
      const errMsg = e?.response?.data?.response_data?.message || e?.response?.data?.error?.message || '';
      if (status === 429 || errCode === 429) {
        setError('Too many login attempts. Please try again later.');
      } else {
        setError(errMsg || 'Invalid email or password.');
      }
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    if (!form.password) { setError('Password is required.'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (form.password !== form.confirm_password) { setError('Passwords do not match.'); return; }
    setLoading(true); setError('');
    try {
      const { risk_portal_register } = await import('../../services');
      const res = await risk_portal_register({ token: inviteToken, password: form.password, confirm_password: form.confirm_password });
      const data = res?.response_data?.data || res?.statusCode?.response_data?.data;
      if (data?.email) {
        setMessage(`Password set successfully for ${data.email}. You can now log in.`);
        setMode('login');
        setForm({ ...form, email: data.email, password: '', confirm_password: '' });
      } else {
        setError(res?.response_data?.message || res?.error?.message || 'Registration failed.');
      }
    } catch (e) {
      setError('Registration failed. The invite link may be expired.');
    }
    setLoading(false);
  };

  return (
    <main className="lg">
      <aside className="lg-stage">
        <Clouds />
        <div className="lg-stage-inner">
          <div className="lg-logo lg-rise" style={{ '--i': 0 }}>
            <img src={foreseeLogo} alt="Foresee Security" />
            <div className="lg-brand-text">
              <span className="lg-brand-name">FORESEE</span>
              <span className="lg-brand-sub">SECURITY</span>
            </div>
          </div>
          <div className="lg-hero">
            <p className="lg-eyebrow-hero lg-rise" style={{ '--i': 1 }}>
              Risk management platform
            </p>
            <h2 className="lg-headline lg-rise" style={{ '--i': 2 }}>
              Manage risk{' '}
              <span className="lg-accent">before it costs you.</span>
            </h2>
            <ul className="lg-modules">
              {MODULES.map(({ title, code, tag, icon }, i) => (
                <li className="lg-rise" style={{ '--i': 3 + i }} key={code}>
                  <div className="lg-module-top">
                    <span className="lg-module-ico">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        {icon}
                      </svg>
                    </span>
                    <span className="lg-pill">{tag}</span>
                  </div>
                  <b>{title}</b>
                  <small>{code}</small>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </aside>

      <section className="lg-panel" aria-labelledby="lg-rp2-title" onPointerMove={trackPointer}>
        {otpChallenge ? (
          <LoginOtpStep challenge={otpChallenge} onVerified={completeLogin} onRestart={restartLogin}/>
        ) : (
        <div className="lg-wrap">
          <header className="lg-form-header">
            <p className="lg-eyebrow">
              <LockIcon /> {mode === 'register' ? 'Set password' : 'Risk portal sign in'}
            </p>
            <h1 className="lg-title" id="lg-rp2-title">
              {mode === 'register' ? 'Set your password' : 'Welcome back'}
            </h1>
            <p className="lg-lead">
              {mode === 'register'
                ? 'Complete your registration to access the Risk Portal.'
                : 'Sign in to access the Risk Portal.'}
            </p>
          </header>

          {message && (
            <div className="lg-alert lg-alert--success" role="status">
              <CheckCircleIcon /> <span>{message}</span>
            </div>
          )}
          {error && (
            <div className="lg-alert lg-alert--error" role="alert">
              <AlertCircleIcon /> <span>{error}</span>
            </div>
          )}

          {mode === 'login' && (
            <form className="lg-form" noValidate onSubmit={e => { e.preventDefault(); handleLogin(); }}>
              <Field
                name="email"
                type="email"
                label="Work email"
                icon={<MailIcon />}
                value={form.email}
                onChange={handleChange}
                autoComplete="username"
              />
              <Field
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                icon={<LockIcon />}
                value={form.password}
                onChange={handleChange}
                autoComplete="current-password"
                trailing={<EyeButton show={showPassword} onToggle={() => setShowPassword(v => !v)} />}
              />
              <div className="lg-row">
                <span />
                <button type="button" className="lg-link" onClick={() => navigate('/risk-portal/forgot-password')}>
                  Forgot password?
                </button>
              </div>
              <button
                type="submit"
                className="lg-submit lg-spot"
                disabled={loading}
                aria-busy={loading}
                onPointerMove={trackPointer}
              >
                {loading ? (
                  <span className="lg-submit-label">
                    <span className="lg-spin" aria-hidden="true" /> Signing in...
                  </span>
                ) : (
                  <span className="lg-submit-label">
                    Sign in <ArrowRightIcon />
                  </span>
                )}
                {loading && <i className="lg-scan" aria-hidden="true" />}
              </button>
            </form>
          )}

          {mode === 'register' && (
            <form className="lg-form" noValidate onSubmit={e => { e.preventDefault(); handleRegister(); }}>
              <Field
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="New password"
                icon={<LockIcon />}
                value={form.password}
                onChange={handleChange}
                autoComplete="new-password"
                trailing={<EyeButton show={showPassword} onToggle={() => setShowPassword(v => !v)} />}
              />
              <Field
                name="confirm_password"
                type={showConfirm ? 'text' : 'password'}
                label="Confirm password"
                icon={<LockIcon />}
                value={form.confirm_password}
                onChange={handleChange}
                autoComplete="new-password"
                trailing={<EyeButton show={showConfirm} onToggle={() => setShowConfirm(v => !v)} />}
              />
              <button
                type="submit"
                className="lg-submit lg-spot"
                disabled={loading}
                aria-busy={loading}
                onPointerMove={trackPointer}
              >
                {loading ? (
                  <span className="lg-submit-label">
                    <span className="lg-spin" aria-hidden="true" /> Setting password...
                  </span>
                ) : (
                  <span className="lg-submit-label">
                    Set password and continue <ArrowRightIcon />
                  </span>
                )}
                {loading && <i className="lg-scan" aria-hidden="true" />}
              </button>
            </form>
          )}
        </div>
        )}

        <p className="lg-trust">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4.5" y="10.5" width="15" height="10" rx="3"/>
            <path d="M8 10.5V8a4 4 0 018 0v2.5"/>
          </svg>
          Protected by enterprise-grade security. Foresee Security - Risk Portal
        </p>
      </section>
    </main>
  );
}

export default RiskPortalAuth;
